import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Building2 } from 'lucide-react';
import companyService from '../services/company.service';
import { jobService } from '../services/job.service';
import { toast } from 'react-toastify';

const GenerateButton = ({ loading, onClick }) => {
  return (
    <button
      onClick={onClick}
      type="submit"
      disabled={loading}
      className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-3 relative overflow-hidden"
    >
      {loading ? (
        <>
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle 
              className="opacity-25" 
              cx="12" 
              cy="12" 
              r="10" 
              stroke="currentColor" 
              strokeWidth="4" 
              fill="none" 
            />
            <path 
              className="opacity-75" 
              fill="currentColor" 
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" 
            />
          </svg>
          <span>Generating...</span>
        </>
      ) : (
        <>
          {/* AI Circuit Icon */}
          <svg 
            className="w-5 h-5" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            <circle cx="12" cy="12" r="3"/>
            <line x1="12" y1="2" x2="12" y2="22"/>
          </svg>
          <span>Generate</span>
          {/* Particle Effect Dots */}
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
          </span>
        </>
      )}
    </button>
  );
};

const CreateJobDescription = () => {
  const [error, setError] = useState(null); 
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    jobTitle: '',
    department: '',
    qualifications: '',
    responsibilities: '',
    companyId: '' // Add company ID field
  });
  
  // Add validation state
  const [errors, setErrors] = useState({
    jobTitle: '',
    department: '',
    qualifications: '',
    responsibilities: '',
    companyId: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  
  // Company selection state
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [showCompanyModal, setShowCompanyModal] = useState(false);

  // Load departments from localStorage on component mount
  useEffect(() => {
    const savedDepartments = localStorage.getItem('departments');
    if (savedDepartments) {
      setDepartments(JSON.parse(savedDepartments));
    } else {
      const defaultDepartments = ['Design', 'Management', 'Finance', 'Business', 'Development'];
      setDepartments(defaultDepartments);
      localStorage.setItem('departments', JSON.stringify(defaultDepartments));
    }
  }, []);

  // Load companies on component mount
  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const response = await companyService.getAllCompanies();
      if (response.success) {
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleAddDepartment = () => {
    if (!newDepartmentName.trim()) return;

    setDepartments(prev => {
      const updated = [...prev, newDepartmentName.trim()];
      localStorage.setItem('departments', JSON.stringify(updated));
      return updated;
    });
    setNewDepartmentName('');
    setShowAddDepartment(false);
  };

  const handleRemoveDepartment = (dept) => {
    setDepartments(prev => {
      const updated = prev.filter(d => d !== dept);
      localStorage.setItem('departments', JSON.stringify(updated));
      return updated;
    });
  };

  const validateForm = () => {
    const newErrors = {
      jobTitle: '',
      department: '',
      qualifications: '',
      responsibilities: '',
      companyId: ''
    };

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }

    if (!formData.department) {
      newErrors.department = 'Please select a department';
    }

    if (!formData.qualifications.trim()) {
      newErrors.qualifications = 'Qualifications are required';
    }

    if (!formData.responsibilities.trim()) {
      newErrors.responsibilities = 'Responsibilities are required';
    }

    if (!formData.companyId) {
      newErrors.companyId = 'Please select a company';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDepartmentSelect = (dept) => {
    setFormData(prev => ({
      ...prev,
      department: dept
    }));
    setErrors(prev => ({
      ...prev,
      department: ''
    }));
  };

  const handleCompanySelect = (companyId) => {
    setFormData(prev => ({
      ...prev,
      companyId: companyId
    }));
    setErrors(prev => ({
      ...prev,
      companyId: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await jobService.createJob({
        ...formData,
        companyId: formData.companyId // Include company ID in the request
      });

      if (response.data) {
        navigate(`/jobs/${response.data._id}`);
      } else {
        setError('Failed to create job');
      }
    } catch (error) {
      console.error('Error creating job:', error);
      setError(error?.response?.data?.message || error?.message || 'An error occurred while creating the job');
    } finally {
      setLoading(false);
    }
  };

  const selectedCompany = companies.find(c => c._id === formData.companyId);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header with Hello Message */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-2">
          <span className="text-orange-500">Hello,</span> I'm HRgpt!
        </h1>
      </div>

      {/* Steps Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-gradient-to-r from-orange-500 to-yellow-500 p-6 rounded-xl text-white">
          <h2 className="text-lg font-semibold mb-2">01 Enter Job Details</h2>
          <p className="text-sm opacity-90">
            Enter job details such as Job title, Department, Qualifications, Employment type, etc.
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">02 Generate JD & KRA</h2>
          <p className="text-sm text-gray-600">
            HRgpt will generate a custom tailored Job Description & KRA that fits your company the best
          </p>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Company Selection */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Select Company *
              </label>
              <button
                type="button"
                onClick={() => setShowCompanyModal(true)}
                className="flex items-center gap-1 text-orange-500 hover:text-orange-600"
              >
                <Plus className="w-4 h-4" />
                <span>Add Company</span>
              </button>
            </div>
            
            {companiesLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-orange-500 border-t-transparent"></div>
                <span className="ml-2 text-gray-500">Loading companies...</span>
              </div>
            ) : companies.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
                <Building2 className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p className="text-gray-500 mb-2">No companies found</p>
                <button
                  type="button"
                  onClick={() => setShowCompanyModal(true)}
                  className="text-orange-500 hover:text-orange-600 font-medium"
                >
                  Add your first company
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {companies.map((company) => (
                  <div
                    key={company._id}
                    onClick={() => handleCompanySelect(company._id)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      formData.companyId === company._id
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Building2 size={16} className="text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">{company.name}</h3>
                        {company.industry && (
                          <p className="text-sm text-gray-500 truncate">{company.industry}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {errors.companyId && (
              <p className="mt-1 text-sm text-red-500">{errors.companyId}</p>
            )}
          </div>

          {/* Job Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Title
            </label>
            <input
              type="text"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleInputChange}
              placeholder="Enter Job Title"
              className={`w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400
                ${errors.jobTitle ? 'border-red-500' : 'border-gray-200'}`}
              required
            />
            {errors.jobTitle && (
              <p className="mt-1 text-sm text-red-500">{errors.jobTitle}</p>
            )}
          </div>

          {/* Department Selection */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Select Department
              </label>
              <button
                type="button"
                onClick={() => setShowAddDepartment(true)}
                className="flex items-center gap-1 text-orange-500 hover:text-orange-600"
              >
                <Plus className="w-4 h-4" />
                <span>Add Department</span>
              </button>
            </div>
            
            {showAddDepartment && (
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newDepartmentName}
                  onChange={(e) => setNewDepartmentName(e.target.value)}
                  placeholder="Enter department name"
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400"
                />
                <button
                  type="button"
                  onClick={handleAddDepartment}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddDepartment(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            )}
            
            <div className="flex flex-wrap gap-2">
              {departments.map((dept) => (
                <div key={dept} className="relative group">
                  <button
                    type="button"
                    onClick={() => handleDepartmentSelect(dept)}
                    className={`px-4 py-2 rounded-lg border transition-all ${
                      formData.department === dept
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300'
                    }`}
                  >
                    {dept}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveDepartment(dept)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            {errors.department && (
              <p className="mt-1 text-sm text-red-500">{errors.department}</p>
            )}
          </div>

          {/* Qualifications */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Qualifications
            </label>
            <textarea
              name="qualifications"
              value={formData.qualifications}
              onChange={handleInputChange}
              placeholder="Enter job qualifications..."
              rows={4}
              className={`w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400
                ${errors.qualifications ? 'border-red-500' : 'border-gray-200'}`}
              required
            />
            {errors.qualifications && (
              <p className="mt-1 text-sm text-red-500">{errors.qualifications}</p>
            )}
          </div>

          {/* Responsibilities */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Responsibilities
            </label>
            <textarea
              name="responsibilities"
              value={formData.responsibilities}
              onChange={handleInputChange}
              placeholder="Enter job responsibilities..."
              rows={4}
              className={`w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400
                ${errors.responsibilities ? 'border-red-500' : 'border-gray-200'}`}
              required
            />
            {errors.responsibilities && (
              <p className="mt-1 text-sm text-red-500">{errors.responsibilities}</p>
            )}
          </div>

          {/* Generate Button */}
          <div className="flex justify-center pt-6">
            <GenerateButton loading={loading} onClick={handleSubmit} />
          </div>
        </form>
      </div>

      {/* Company Creation Modal */}
      {showCompanyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Add New Company</h2>
                <button
                  onClick={() => setShowCompanyModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="text-center py-8">
                <Building2 size={48} className="mx-auto text-orange-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Add Company</h3>
                <p className="text-gray-600 mb-4">Please add a company first to create jobs</p>
                <button
                  onClick={() => {
                    setShowCompanyModal(false);
                    navigate('/companies');
                  }}
                  className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Go to Companies
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateJobDescription;
